import { AfterViewInit, Component, Inject, Input, ViewChild } from '@angular/core';

import { Observable, Subject, combineLatest } from 'rxjs';
import { map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';

import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { MandatoryMap } from '../../../../shared/helpers/mandatory-map.class';
import { SubjectRelay } from '../../../../shared/commands/subject-relay.class';

import { EntrepriseService } from '../../../entreprises/services/entreprise.service';
import { UserService } from '../../user/services/user.service';

import { IntervenantsFilters } from '../entities/intervenants-filters.entity';
import { IntervenantService } from '../services/intervenant.service';
import { IntervenantTLR } from '../entities/intervenant-tlr.entity';

// import { EditGroupementDialogComponent } from './edit-groupement-dialog.component';

type DisplayedIntervenant = {
	entity: IntervenantTLR,
	nomEntreprise: string,
}

@Component({
	selector: 'intervenants-table',
	templateUrl: 'intervenants-table.html',
	styleUrls: ['intervenants-table.css']
})
export class IntervenantsTableComponent implements AfterViewInit {
	private data: DisplayedIntervenant[] = [];

	reloadIntervenants$ = new Subject<void>();
	exportRequests$ = new Subject<void>();
	editIntervenantRequests$ = new Subject<IntervenantTLR | null>();
		
	isLoading: boolean = true;
	dataSource: MatTableDataSource<DisplayedIntervenant> = new MatTableDataSource(this.data);
	displayedColumns: string[] = [
		'id',
		'nom',
		'prenom',
		'cadreEmploi',
		'nomEntreprise',
		'telephone',
		'email',
		'interne',
		'travailleurDetache',
		'obsolete',
		'actions',
	];
	
	constructor(
		@Inject(EntrepriseService) entrepriseService: EntrepriseService,
		@Inject(IntervenantService) private intervenantService: IntervenantService,
	) {
		let entreprisesById$ = entrepriseService.getEntreprisesTLRMapById().pipe(
			map(
				data => new MandatoryMap(data, "Entreprise")
			)
		);
		
		let intervenants$ = combineLatest([this.reloadIntervenants$]).pipe(
			tap(
				() => this.isLoading = true
			),
			mergeMap(
				([_]) => this.intervenantService.getIntervenantsTLRList()
			),
		);
		
		let displays$ = combineLatest([entreprisesById$, intervenants$]).pipe(
			map(
				([entreprisesById, intervenants]) => {
					let displays = intervenants.filter(
						$ => !$.nom.startsWith('INTERVENANT FICTIF') && !$.nom.startsWith('VALIDEUR FICTIF')
					).map($ => ({
						entity: $,
						nomEntreprise: $.entrepriseId === null ? '' : entreprisesById.get($.entrepriseId).nom,
					} as DisplayedIntervenant));
					
					return displays;
				}
			)
		);
		
		displays$.subscribe(
			(displays) => {
				this.data = displays;
				this.dataSource.data = displays;
				this.isLoading = false;
			}
		)
		
		this.filters$.subscribe(
			(filters) => {
				// Ampliquer les criteres de filtrage
				this.dataSource.filter = JSON.stringify(filters);
			}
		);
		
		let newExportNeeded$ = this.exportRequests$.pipe(
			withLatestFrom(this.filters$),
		);
		
		newExportNeeded$.subscribe(
			([_, filters]) => {
				// Lancer la generation et le download du excel en fonction des criteres et de l'etat choisi
				this.intervenantService.export(filters).subscribe(
					(blob: Blob) => {
						const objectUrl: string = URL.createObjectURL(blob);
						const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;
						a.href = objectUrl;
						a.download = "export-intervenants.xls";
						document.body.appendChild(a);
						a.click();
						
						document.body.removeChild(a);
						URL.revokeObjectURL(objectUrl);
					},
					(err: any) => {
						alert("Erreur lors du téléchargement de l'export");
						console.log('Could not generate the file, error: ', err);
					}
				);		
				
			}
		)
	}

	// Elements injectes necessaires a la gestion du tableau
	@ViewChild(MatPaginator) paginator!: MatPaginator;
	@ViewChild(MatSort) sort!: MatSort;

	// Liaison de l'observable alertante des changements de filtres
	private filters$ = new SubjectRelay<IntervenantsFilters>();
	@Input() set filtersSubject(obs: Observable<IntervenantsFilters> | undefined) {
		this.filters$.registerSource(obs);
	}

	ngAfterViewInit() {
		// Initialisation du dataSource
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
		
		// Definition de la facon dont les id de colonnes sont traduites en valeurs pour le tri 
		this.dataSource.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'nomEntreprise': {
					return item.nomEntreprise;
				}
				default: {
					// NB: etrangement, juste faire "entity[property]" cause une erreur de compilation
					const entity = item.entity;
					const value = (entity as { [key: string]: any })[property];
					return value;
				}
			}
		};
		
		// Definition de comment le tableau applique les filtres
		this.dataSource.filterPredicate = (display: DisplayedIntervenant, filterStr: string) => {
			const filters: IntervenantsFilters = JSON.parse(filterStr);
			
			// Commencer par les filtres binaires car ils coutent moins a evaluer
			if (this.booleanFilter(filters.statut, !display.entity.obsolete)) {
				return false;
			}
			if (this.booleanFilter(filters.interne, display.entity.interne)) {
				return false;
			}
			if (this.booleanFilter(filters.travailleurDetache, display.entity.travailleurDetache)) {
				return false;
			}
			
			// Evaluer ensuite les filtres textuels			
			if (this.stringFilter(filters.nom, display.entity.nom)) {
				return false;
			}
			if (this.stringFilter(filters.prenom, display.entity.prenom)) {
				return false;
			}
			if (this.stringFilter(filters.nomEntreprise, display.nomEntreprise)) {
				return false;
			}
			
			return true;
		}
		
		// Demarrage du chargement initial
		this.reloadIntervenants$.next();
	}
	
	// Fonctions d'aide au filtrage
	private booleanFilter = (criteria: boolean | null, value: boolean | null) => criteria !== null && value !== criteria;
	private stringFilter = (criteria: string | null, value: string | null) => criteria && (!value || value.toLowerCase().indexOf(criteria.toLowerCase()) < 0);
	
	delete(intervenant: IntervenantTLR) {
		if (!confirm('Confirmez-vous la suppression de l\' intervenant ' + intervenant.nom + ' ' + intervenant.prenom + ' ?')) {
			return;
		}

		this.intervenantService.delete(intervenant.id).subscribe({
			next: (_result) => {
				// rafraichir l'affichage
				this.reloadIntervenants$.next();

				// afficher le confirmation de suppression
				alert("L'intervenant " + intervenant.nom + " " + intervenant.prenom + " a été supprimé");
			},
			error: (response) => {
				if (response.status === 400 && response.error && response.error.message) {
					alert(response.error.message);
				} else {
					alert("Erreur lors de la suppression de cet intervenant (" + response.status + ")");
				}
			}
		});
	}
}